import { useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { IStockOrderDetail, IStockOrderProduct } from '../../../../../../../types/stockOrders';
import NudosMaskedNumberInput from '../../../../../../../components/NudosComponents/NudosMaskedNumberInput/NudosMaskedNumberInput';
import { QRDownloadButton } from '../../../../../../../components';
import { updateProductValues } from '../../../../../../../services/stockOrders';
import useStockReservationState from 'state/useStockReservationState';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';
import {
  NudosStaticAlertInComponent,
  NudosTextInput,
  NudosSelectButton
} from '../../../../../../../components/NudosComponents';
import BordAcordeon from '../../../../../../../components/BordAcordeon/BordAcordeon';
import BordOneToneIcon from '../../../../../../../components/NudosComponents/BordOneToneIcon/BordOneToneIcon';
import './StockOrderListOfProducts.scss';

interface StockOrderListOfProductsProps {
  listOfProducts: IStockOrderDetail['listOfProducts'];
  updateWithoutLoaders?: () => Promise<void>;
}

/**
 * Componente que muestra la lista de productos de una orden de stock y permite editar sus valores
 * @param {StockOrderListOfProductsProps} props - Props del componente
 * @returns {JSX.Element} Componente StockOrderListOfProducts
 */
const StockOrderListOfProducts = ({ listOfProducts, updateWithoutLoaders }: StockOrderListOfProductsProps) => {
  const { uuid: stockOrderUuid } = useParams<{ uuid: string }>();

  const { stockProductsSelected, setStockProductsSelected } = useStockReservationState();
  const [editedValues, setEditedValues] = useState<Record<string, number>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [expandedProducts, setExpandedProducts] = useState<Record<string, boolean>>({});

  const handleValueChange = useCallback(
    (productId: number, value?: string, currentSku?: string) => {
      if (!value || !currentSku) return;
      const numericValue = parseFloat(value);
      if (!isNaN(numericValue)) {
        const productsWithSameSku =
          listOfProducts?.reduce((acc: number[], product) => {
            if (product.sku === currentSku) {
              product.items.forEach(item => {
                acc.push(item.productId);
              });
            }
            return acc;
          }, []) || [];

        setEditedValues(prev => {
          const newValues = { ...prev };

          newValues[productId] = numericValue;

          productsWithSameSku.forEach(pid => {
            if (pid !== productId) {
              newValues[pid] = numericValue;
            }
          });
          return newValues;
        });
      }
    },
    [listOfProducts]
  );

  const renderPills = (pills: Record<string, string> | string | null) => {
    if (!pills || typeof pills === 'string') return null;

    return Object.entries(pills).map(([key, value]) => (
      <div key={key} className="pillContainer">
        <div className="pillTitle">{key}</div>
        <div className="pillValue">{value}</div>
      </div>
    ));
  };

  const handleToggleAccordion = useCallback((sku: string) => {
    setExpandedProducts(prev => ({
      ...prev,
      [sku]: !prev[sku]
    }));
  }, []);

  const handleSave = useCallback(async () => {
    if (Object.keys(editedValues).length === 0) return;

    try {
      setIsSaving(true);
      const valuesToUpdate = Object.entries(editedValues).map(([productId, value]) => ({
        productId: parseInt(productId),
        value
      }));

      await updateProductValues(stockOrderUuid, valuesToUpdate);
      setEditedValues({});
      if (updateWithoutLoaders) {
        await updateWithoutLoaders();
      }
    } catch (error) {
      displayErrorNotification({
        customJSXMessage: <>Error al actualizar los valores</>
      });
    } finally {
      setIsSaving(false);
    }
  }, [editedValues, stockOrderUuid, updateWithoutLoaders]);

  const handleCheckProductButton = (product: IStockOrderProduct) => {
    if (!product.items?.length) return;

    const areAllItemsSelected =
      stockProductsSelected?.sku === product.sku && stockProductsSelected?.items?.length === product.items.length;

    if (areAllItemsSelected) {
      setStockProductsSelected(null);
    } else {
      setStockProductsSelected({
        sku: product.sku,
        items: product.items.map(item => ({
          serial: item.serial,
          productId: item.productId,
          value: item.value,
          currency: item.currency
        }))
      });
    }
  };

  return (
    <div className="stockOrderListOfProducts">
      {Object.keys(editedValues).length > 0 && (
        <button onClick={handleSave} disabled={isSaving} className="floatingButton">
          {isSaving ? 'Guardando...' : 'Guardar cambios'}
        </button>
      )}
      {listOfProducts?.map(product => (
        <div key={product.sku} className="stockOrderProductCard">
          <div className="productHeader">
            <div className="productNameContainer">
              <NudosSelectButton
                iconName="checkmark"
                isButtonActive={
                  stockProductsSelected?.sku === product.sku &&
                  stockProductsSelected?.items?.length === product.items.length
                }
                handleClickButton={() => handleCheckProductButton(product)}
                componentSize="xs w-2 h-2"
              />
              <div className="productName">{product.name}</div>
            </div>
            <div className="accordionToggleButton" onClick={() => handleToggleAccordion(product.sku)}>
              <span className="accordionToggleText">
                {expandedProducts?.[product?.sku] ? 'Ocultar unidades' : 'Ver unidades'}
              </span>
              <BordOneToneIcon
                variant="arrowHeadDown"
                standardSize={10}
                className={`accordionArrow ${expandedProducts?.[product?.sku] ? 'accordionArrowRotated' : ''}`}
              />
            </div>
          </div>
          <div className="productInfo">
            <div className="flex items-start gap-6">
              {renderPills(product.pills)}
              <div className="productSku">
                <div>SKU Catálogo</div>
                <div className="productSkuValue">{product.sku}</div>
              </div>
              <div className="productQuantity">
                <div>Total de Unid.</div>
                <div className="productQuantityValue">{product.quantity}</div>
              </div>
            </div>
          </div>

          <BordAcordeon isExpanded={expandedProducts?.[product?.sku]}>
            <div className="listOfSerials">
              {product.items.map((item, index) => {
                const qrData = {
                  serial: item?.serial,
                  productId: item?.productId,
                  orgName: 'Stock Propio'
                };

                const showAlertMessage = product?.items?.length > 1 && index === 0;

                const handleCheckUnitButton = (serial: string) => {
                  if (stockProductsSelected?.items?.some(item => item.serial === serial)) {
                    if (stockProductsSelected.items.length === 1) {
                      setStockProductsSelected(null);
                    } else {
                      setStockProductsSelected({
                        sku: product.sku,
                        items: stockProductsSelected.items.filter(item => item.serial !== serial)
                      });
                    }
                  } else {
                    setStockProductsSelected({
                      sku: product.sku,
                      items: [
                        ...(stockProductsSelected?.items ?? []),
                        { serial, productId: item.productId, value: item.value, currency: item.currency }
                      ]
                    });
                  }
                };

                return (
                  <div key={item.serial}>
                    <NudosSelectButton
                      iconName="checkmark"
                      isButtonActive={stockProductsSelected?.items?.some(
                        selectedItem => selectedItem.serial === item.serial
                      )}
                      handleClickButton={() => handleCheckUnitButton(item.serial)}
                      componentSize="xs w-2 h-2"
                    />
                    <div className="serialItem">
                      <NudosTextInput
                        label="Serial"
                        defaultValue={item?.serial ?? 'Sin serial'}
                        isFilled
                        isDeactivated
                        placeholder=""
                        componentSize="small-184"
                      />
                      <NudosMaskedNumberInput
                        label="Costo con IVA"
                        currentValue={String(editedValues[item.productId] ?? item?.value)}
                        isFilled
                        isDeactivated={index !== 0}
                        placeholder=""
                        componentSize="small-184"
                        maskPrefix="$"
                        maskSufix={` ${item?.currency}`}
                        numberOfDecimals={2}
                        handleChange={value => index === 0 && handleValueChange(item.productId, value, product.sku)}
                      />
                      <div className="stokOrderQrButtonContainer">
                        <QRDownloadButton QRData={qrData} />
                      </div>
                    </div>
                    {showAlertMessage && (
                      <NudosStaticAlertInComponent
                        customClassname="customNudosStaticAlertInComponent ml-4 w-max"
                        type="information"
                        alertOptions={{
                          title: (
                            <>
                              Al cambiar el costo con IVA en esta unidad, se actualizará automáticamente en todas las
                              demás unidades de esta herramienta.
                            </>
                          )
                        }}
                        customTitleClass="customTitleAlert"
                        hideTitleClass
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </BordAcordeon>
        </div>
      ))}
    </div>
  );
};

export default StockOrderListOfProducts;
